import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { categories, getComicsByCategory } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

interface CategoryInfo {
  title: string;
  desc: string;
  bannerUrl: string;
}

const allCategoryInfo: Record<string, CategoryInfo> = {
  hanman: {
    title: '韩漫',
    desc: '精选韩国漫画，恋爱、奇幻、热血题材一网打尽',
    bannerUrl: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20artistic%20illustration%20background%20with%20warm%20romantic%20tones%2C%20soft%20pastel%20colors%2C%20elegant%20Korean%20webtoon%20aesthetic%2C%20minimalist%20composition%2C%20subtle%20gradient%2C%20no%20text&width=1920&height=600&seq=category-banner-hanman&orientation=landscape',
  },
  riman: {
    title: '日漫',
    desc: '经典日本漫画，热血战斗、奇幻冒险、校园喜剧应有尽有',
    bannerUrl: 'https://readdy.ai/api/search-image?query=Japanese%20manga%20artistic%20illustration%20background%20with%20dynamic%20composition%2C%20bold%20ink%20style%2C%20warm%20amber%20and%20coral%20tones%2C%20shonen%20manga%20aesthetic%2C%20clean%20minimal%2C%20no%20text&width=1920&height=600&seq=category-banner-riman&orientation=landscape',
  },
  guoman: {
    title: '国漫',
    desc: '优秀国产漫画，仙侠玄幻、都市异能、历史文化精彩纷呈',
    bannerUrl: 'https://readdy.ai/api/search-image?query=Chinese%20manhua%20artistic%20illustration%20background%20with%20traditional%20ink%20wash%20painting%20style%2C%20elegant%20warm%20colors%2C%20Chinese%20cultural%20elements%2C%20modern%20minimalist%20composition%2C%20soft%20atmospheric%20lighting%2C%20no%20text&width=1920&height=600&seq=category-banner-guoman&orientation=landscape',
  },
};

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const activeType = type && ['hanman', 'riman', 'guoman'].includes(type) ? type : 'hanman';
  const [comics, setComics] = useState<Comic[]>([]);
  const info = allCategoryInfo[activeType];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeType]);

  useEffect(() => {
    setComics(getComicsByCategory(activeType));
  }, [activeType]);

  return (
    <>
      <Navbar />
      <main>
        <div className="pt-20 pb-0 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 py-3" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </div>
              <span className="text-foreground-800 font-medium">{info.title}</span>
            </nav>
          </div>
        </div>

        <section className="relative h-[200px] md:h-[260px] flex items-center justify-center overflow-hidden">
          <img
            src={info.bannerUrl}
            alt={info.title}
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-2xl md:text-3xl font-heading text-white mb-2">{info.title}</h1>
            <p className="text-sm text-white/75">{info.desc}</p>
          </div>
        </section>

        <section className="bg-background-50 border-b border-background-200">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-3">
            <div className="flex items-center gap-1 overflow-x-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.key}
                  to={`/category/${cat.key}`}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeType === cat.key
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {cat.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {comics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}